import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Socket } from 'socket.io-client';
import MicButton from '../common/MicButton';
import Disclaimer from '../common/Disclaimer';
import DisclaimerToast from '../common/DisclaimerToast';
import AvatarRing from '../common/AvatarRing';
import View from '../common/View';
import Spinner from '../common/Spinner';
import SuggestedPrompts from '../common/SuggestedPrompts';
import BottomNav, {
  BottomNavTab,
  buildDefaultNavItems,
} from '../common/BottomNav';
import InterpreterChip from '../common/InterpreterChip';
import DrawerHost from '../components/drawers';
import WebDataCarousel, {
  type WebDataItem,
} from '../components/companion/WebDataCarousel';
import CompanionFeedOverlay from '../components/companion/CompanionFeedOverlay';
import LiveVideoOverlay from '../components/companion/LiveVideoOverlay';
import LiveCameraOverlay from '../components/companion/LiveCameraOverlay';
import InterpreterOverlay from '../components/interpreter/InterpreterOverlay';
import TripWizardSheet from '../components/itinerary/TripWizardSheet';
import { useLanguage } from '../hooks/useLanguage';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useDrawer, DrawerId } from '../context/DrawerContext';
import { useUserContext } from '../context/UserContext';
import { useEntitlements } from '../context/EntitlementsContext';
import { useAuthContext } from '@wg/hooks/auth';
import { useGuestUX } from '@wg/hooks/guest';
import { contentApi } from '../client';
import GuestCtaBanner from '../common/GuestCtaBanner';
import { useAudioChat } from '../hooks/useAudioChat';
import { useChatSession } from '../context/ChatSessionContext';
import AgentChip from '../components/AgentChip';
import { useSocket, useSocketEvent } from '../context/SocketContext';
import { useLanguage as useLanguageContext } from '../context/LanguageContext';
import { InterpreterProvider } from '../context/InterpreterContext';
import useInterpreterMode from '../hooks/useInterpreterMode';
import useCompanionFeed from '../hooks/useCompanionFeed';
import { httpClient } from '../http';
import { bootstrapChatSession } from '../api/chatApi';
import { registerServiceWorker } from '../lib/webPush';
import './Dashboard.css';

/**
 * Dashboard — RN-parity hero. Sources of truth:
 *  • greeting/name → useUserContext
 *  • avatar status ring → useEntitlements (effectiveBalance, hasUnmeteredAccess)
 *  • mic state + permission → useAudioChat (real getUserMedia + analyser)
 *  • suggested prompts → useSuggestedPrompts (auto-rotating slideshow)
 *  • feature-flag-gated nav and chips → useFeatureFlags
 *  • bottom nav opens drawers (no routes) — see DrawerHost
 */
const Dashboard: React.FC = () => {
  const { t, LogoType } = useLanguage();
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const {
    openDrawer,
    closeDrawer,
    currentDrawer,
    tripWizardOpen,
    closeTripWizard,
  } = useDrawer();
  const { user } = useUserContext();
  const { isGuest, sessionInfo } = useAuthContext();
  const guestUX = useGuestUX({
    isGuest,
    firstName: sessionInfo?.firstName,
    userFirstName: user?.firstName,
    language: undefined,
    contentApi,
  });
  const { selectedSessionId, agent, setSelectedSessionId, setAgent } =
    useChatSession();
  const audioChat = useAudioChat();
  const {
    effectiveBalance,
    hasUnmeteredAccess,
    allowance,
    billingEnabled,
    refreshBalance,
  } = useEntitlements();
  const { connect: socketConnect, socket } = useSocket();
  const { selectedLanguage } = useLanguageContext();

  // Stable socket ref for hooks that need a MutableRefObject<Socket>.
  const socketRef = useRef<Socket | null>(null);
  socketRef.current = socket;

  const interpreter = useInterpreterMode({
    socketRef,
    userLanguage: selectedLanguage,
    sessionId: selectedSessionId ?? undefined,
  });

  // Open the data-channel socket on mount, mirroring RN's preconnect.
  useEffect(() => {
    socketConnect().catch(() => {});
    registerServiceWorker().catch(() => {});
  }, [socketConnect]);

  // Background-fetch the agent persona (avatar + display name) from the
  // chat-sessions API so AvatarRing on the Dashboard reflects the API,
  // mirroring RN's `selectedSession.agentAvatarUrl` chain. NEVER hard-code
  // the persona image.
  useEffect(() => {
    if (selectedSessionId && agent) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await httpClient.get<any>('/functions/v1/chat/sessions');
        if (cancelled) return;
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data?.sessions ?? []);
        const s = arr[0];
        if (s?.id) {
          if (!selectedSessionId) setSelectedSessionId(s.id);
          setAgent({
            id: s.agent_id,
            displayName: s.agent_display_name,
            avatarUrl: s.agent_avatar_url,
            greetings: s.greetings,
          });
          return;
        }
        // Brand-new user (typically a fresh guest) with NO chat sessions yet:
        // the list is empty, so without this we leave `agent` null and the
        // Dashboard shows a generic silhouette with no session — the "no
        // avatar / no session" guest bug. Resolve + create the location-default
        // guide (same bootstrap the mic-tap and RN home use) so the Dashboard
        // shows a real guide avatar and has a ready session.
        const boot = await bootstrapChatSession();
        if (cancelled || !boot?.id) return;
        if (!selectedSessionId) setSelectedSessionId(boot.id);
        if (boot.agent) {
          setAgent({
            id: boot.agent.id,
            displayName: boot.agent.displayName,
            avatarUrl: boot.agent.avatarUrl,
          });
        }
      } catch {
        /* leave agent null — AvatarRing falls back to user.avatarUrl */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSessionId, agent, setSelectedSessionId, setAgent]);

  // Server-pushed balance refreshes (after consumption / IAP).
  useSocketEvent<{ event?: string; remainingSeconds?: number }>(
    'balanceUpdate',
    () => {
      refreshBalance();
    },
  );

  // Server-driven directives mirror RN.
  useSocketEvent('showAccountDrawer', () => openDrawer('wallet'));

  // ── webData → carousel cards (mirrors RN handleCarouselItemsUpdate) ──
  const [carouselItems, setCarouselItems] = useState<WebDataItem[]>([]);
  const [isCarouselMinimized, setIsCarouselMinimized] = useState(true);
  // Realtime Live Vision (Gemini Live) camera overlay — in-memory only.
  const [showLiveVideo, setShowLiveVideo] = useState(false);
  const [showLiveCamera, setShowLiveCamera] = useState(false);

  useSocketEvent<{
    items?: Array<{ id: string; src: { uri?: string; html?: string } }>;
  }>('webData', data => {
    if (!data?.items?.length) return;
    setCarouselItems(prev => {
      const existing = new Set(prev.map(i => i.id));
      const additions: WebDataItem[] = data
        .items!.filter(it => !existing.has(it.id))
        .map(it => ({ id: it.id, src: it.src, updatedAt: Date.now() }));
      if (additions.length === 0) return prev;
      const merged = [...additions, ...prev].sort(
        (a, b) => b.updatedAt - a.updatedAt,
      );
      // RN auto-expands when new items arrive.
      setIsCarouselMinimized(false);
      return merged;
    });
  });

  const removeCarouselItem = useCallback((id: string) => {
    setCarouselItems(prev => {
      const next = prev.filter(i => i.id !== id);
      if (next.length === 0) setIsCarouselMinimized(true);
      return next;
    });
  }, []);

  const companionFeed = useCompanionFeed({
    sessionId: selectedSessionId,
    webDataItems: carouselItems,
    enabled: currentDrawer === 'companion' || currentDrawer === 'chat',
  });

  // Tick the current hour every minute so the greeting rolls morning→afternoon
  // →evening through the day. Previously read new Date().getHours() once inside
  // a useMemo with deps [t]/[], so it froze at the page-load hour (mirrors the
  // RN dashboard, which already ticks currentHour on a 60s interval).
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());
  useEffect(() => {
    const id = setInterval(() => setCurrentHour(new Date().getHours()), 60000);
    return () => clearInterval(id);
  }, []);

  // Greeting parts: the agent's server-resolved localized line for the current
  // time-of-day (emoji baked in, {name} token), plus a no-emoji fallback. Name
  // is substituted in the JSX below where firstName is in scope.
  const greetingParts = useMemo(() => {
    const set = agent?.greetings;
    const tod =
      currentHour < 12
        ? 'morning'
        : currentHour < 18
          ? 'afternoon'
          : currentHour < 21
            ? 'evening'
            : 'night';
    const dbLine = set ? (set[tod] ?? set.evening ?? set.morning) : undefined;
    const fallback =
      currentHour < 12
        ? t('GOOD_MORNING') || 'Good morning'
        : currentHour < 18
          ? t('GOOD_AFTERNOON') || 'Good afternoon'
          : t('GOOD_EVENING') || 'Good evening';
    return { dbLine, fallback };
  }, [currentHour, t, agent?.greetings]);

  const timeOfDay = useMemo<
    'morning' | 'afternoon' | 'evening' | 'night'
  >(() => {
    if (currentHour < 12) return 'morning';
    if (currentHour < 18) return 'afternoon';
    if (currentHour < 22) return 'evening';
    return 'night';
  }, [currentHour]);

  // Mirrors RN Dashboard: drop the comma+name for guests; for authed users
  // show the firstName when present. Never fall back to the email local part.
  const firstName = guestUX.shouldOmitName ? '' : user?.firstName?.trim() || '';

  // Map nav tab → drawer id + feature flag. Mirrors RN FloatingToolbar
  // nav_v2 (Trip | Explore | Book | Deals | Account). Inbox is reached via
  // the BellIcon at top-right, NOT the bottom nav.
  const navToDrawer: Record<BottomNavTab, { id: DrawerId; flag: string }> = {
    trip: { id: 'itinerary', flag: 'itinerary' },
    explore: { id: 'explore', flag: 'explore' },
    book: { id: 'book', flag: 'book' },
    deals: { id: 'deals', flag: 'deals' },
    account: { id: 'settings', flag: '__always_on' },
  };

  // Visible nav items computed from flags. Default true for missing keys so
  // dev sees the full nav (matches RN SCREENSHOT_MODE behaviour). Profile
  // is always present.
  const visibleNavItems = useMemo(
    () =>
      buildDefaultNavItems(t).filter(item => {
        const target = navToDrawer[item.id];
        if (target.flag === '__always_on') return true;
        return isEnabled(target.flag, true);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEnabled, t],
  );

  const handleNavChange = (tab: BottomNavTab) => {
    const target = navToDrawer[tab];
    if (!target) return;
    openDrawer(target.id);
  };

  // RN render-gate (apps/WunderGuide/views/Dashboard.tsx line 2001):
  //   {isFeatureEnabled('interpreter', false) && audioChat.isListening && (
  const showInterpreterChip =
    isEnabled('interpreter', false) && audioChat.isListening;

  const activeNavTab: BottomNavTab | null = useMemo(() => {
    switch (currentDrawer) {
      case 'itinerary':
        return 'trip';
      case 'explore':
        return 'explore';
      case 'book':
        return 'book';
      case 'deals':
        return 'deals';
      case 'settings':
        return 'account';
      default:
        return null;
    }
  }, [currentDrawer]);

  // No fallback prompts. Suggested prompts come from the backend
  // (`/functions/v1/content/sessions/{sessionId}/suggested-prompts`) once a
  // chat session exists; until then the carousel stays empty rather than
  // showing untranslated i18n keys that read like enums.

  // Hold the dashboard until feature flags resolve. Otherwise nav tabs and
  // chips render with their gate defaults, then snap to the resolved values
  // when the API responds — visible flicker, especially on slow mobile.
  if (flagsLoading) {
    return (
      <View className="loading-container">
        <Spinner />
      </View>
    );
  }

  return (
    <InterpreterProvider value={interpreter}>
      <div className="dash">
        <header className="dash__top">
          <span className="dash__top-spacer" />
          <span className="dash__wordmark">
            <LogoType />
          </span>
          <button
            type="button"
            className="dash__icon-btn"
            aria-label="Notifications"
            onClick={() => openDrawer('inbox')}
          >
            <BellIcon />
          </button>
        </header>

        <section className="dash__hero">
          <AvatarRing
            avatarUrl={agent?.avatarUrl ?? user?.avatarUrl}
            effectiveBalance={effectiveBalance}
            hasUnmeteredAccess={hasUnmeteredAccess}
            allowance={allowance ?? undefined}
            billingEnabled={billingEnabled}
            size={172}
            onPress={() => openDrawer(isGuest ? 'guestSignup' : 'wallet')}
          />
          <AgentChip />
          <h1 className="dash__greeting">
            {greetingParts.dbLine
              ? greetingParts.dbLine.replace(
                  '{name}',
                  firstName ? `, ${firstName}` : '',
                )
              : `${greetingParts.fallback}${firstName ? `, ${firstName}` : ''}!`}
          </h1>
          {guestUX.shouldShowBanner && (
            <GuestCtaBanner
              text={guestUX.guestContent.banner.text}
              ctaText={guestUX.guestContent.banner.ctaText}
              onClick={() => openDrawer('guestSignup')}
            />
          )}
          {/* Prompts are inspirational by default — DO NOT auto-start voice
            on tap. RN parity: the pill renders inert unless the backend
            attaches an explicit action. When `action` lands on the API
            payload, wire it through here. */}
          <SuggestedPrompts
            sessionId={selectedSessionId}
            enabled={isEnabled('chat', true)}
            language={selectedLanguage}
            timeOfDay={timeOfDay}
          />
        </section>

        <section className="dash__mic-area">
          <div className="dash__mic-wrap">
            <MicButton
              isListening={audioChat.isListening}
              isConnecting={audioChat.isConnecting}
              hasRemoteParticipant={audioChat.hasRemoteParticipant}
              onPress={audioChat.toggleListening}
              micAudioLevel={audioChat.micAudioLevel}
              botAudioLevel={audioChat.botAudioLevel}
              isBotWorking={audioChat.isBotWorking}
              wrapperSize={216}
            />
          </div>

          {/* RN accessoryRight — chat (always) + interpreter (while listening). */}
          <div className="dash__side-chips">
            <button
              type="button"
              className="dash__chip"
              onClick={() => openDrawer('chat')}
              aria-label="Open chat"
            >
              <ChatBubbleIcon />
            </button>
            {showInterpreterChip && (
              <InterpreterChip onClick={() => openDrawer('interpreter')} />
            )}
          </div>
        </section>

        {/* AI disclaimer — RN parity, two variants:
              - Large viewports (>414px width): persistent inline row above
                the bottom nav. Same content as RN's AudioChat overlay.
              - Small viewports (≤414px, e.g. iPhone SE): ephemeral top
                toast at activation + a small "AI" pill to re-show. The
                inline row was crowding the mic + bottom nav at SE sizes.
            The CSS media-queries on each variant own the show/hide
            switching so React only mounts one tree per breakpoint group. */}
        {audioChat.isListening && audioChat.hasRemoteParticipant && (
          <>
            <div className="dash__disclaimer-row">
              <Disclaimer text={audioChat.disclaimer} />
            </div>
            <DisclaimerToast
              text={audioChat.disclaimer}
              active={audioChat.isListening && audioChat.hasRemoteParticipant}
            />
          </>
        )}

        <BottomNav
          items={visibleNavItems}
          active={activeNavTab}
          onChange={handleNavChange}
        />

        <WebDataCarousel
          items={carouselItems}
          minimized={isCarouselMinimized}
          onMinimize={() => setIsCarouselMinimized(true)}
          onExpand={() => setIsCarouselMinimized(false)}
          onRemove={removeCarouselItem}
        />

        <DrawerHost />

        <CompanionFeedOverlay
          visible={currentDrawer === 'companion'}
          onClose={closeDrawer}
          cards={companionFeed.cards}
          sessions={companionFeed.sessions}
          isVoiceActive={audioChat.isListening}
          onSendText={companionFeed.sendText}
          autoFocusInput
          agentDisplayName={agent?.displayName}
          visionEnabled={
            isEnabled('live_vision') || isEnabled('dining_concierge')
          }
          onScanImage={companionFeed.scanImage}
          onOpenLiveCamera={() => setShowLiveCamera(true)}
          realtimeEnabled={isEnabled('live_vision_realtime')}
          onStartLiveVideo={() => setShowLiveVideo(true)}
        />

        <LiveVideoOverlay
          visible={showLiveVideo}
          onClose={() => setShowLiveVideo(false)}
          setLiveVideoEnabled={audioChat.setLiveVideoEnabled}
        />

        <LiveCameraOverlay
          visible={showLiveCamera}
          onClose={() => setShowLiveCamera(false)}
          sessionId={selectedSessionId}
          mode={
            isEnabled('dining_concierge') && !isEnabled('live_vision')
              ? 'menu'
              : 'scene'
          }
        />

        <InterpreterOverlay
          visible={currentDrawer === 'interpreter'}
          onClose={closeDrawer}
        />

        <TripWizardSheet visible={tripWizardOpen} onClose={closeTripWizard} />
      </div>
    </InterpreterProvider>
  );
};

export default Dashboard;

function BellIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 4 2 5 2 7H4c0-2 2-3 2-7z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}

function ChatBubbleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12a8 8 0 1 1-3.6-6.7" />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="13" cy="12" r="1" fill="currentColor" />
      <circle cx="17" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}
